/**
 * proceduralRegistry.ts — metadata for every available procedural background.
 *
 * Adding a new generator:
 *  1. Implement a WebGL renderer in webgl/<id>.ts (satisfying ProceduralRenderer).
 *  2. Register it in GENERATORS below.
 *  3. Import the renderer lazily in ProceduralBackground.tsx.
 */

import type { SpaceBackground } from '../../../types/spaces';

// ── Palette definitions ───────────────────────────────────────────────────────

export interface Palette {
  id: string;
  label: string;
  /** CSS-compatible color strings used by the generator. */
  colors: string[];
}

// ── Generator descriptor ──────────────────────────────────────────────────────

export interface GeneratorParam {
  id: string;
  label: string;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
}

export type GeneratorId = 'topo' | 'riverNetwork' | 'haze' | 'contours' | 'dotGrid' | 'cuttingMat' | 'starfield' | 'arabesque' | 'domeV2';

export interface GeneratorDescriptor {
  id: GeneratorId;
  label: string;
  description: string;
  palettes: Palette[];
  defaultPalette: string;
  params: GeneratorParam[];
  defaultSeed: number;
}

// ── Generator registry ────────────────────────────────────────────────────────

export const GENERATORS: GeneratorDescriptor[] = [
  {
    id: 'topo',
    label: 'Atlas',
    description: 'Domain-warped world terrain: continents, oceans, coastlines and topographic isolines.',
    defaultSeed: 42,
    defaultPalette: 'terrain',
    // World ramps, deep→peak: [0]=deep water [1]=shallow water
    // [2]=lowland [3]=upland [4]=mountain [5]=peak.
    palettes: [
      { id: 'terrain', label: 'Atlas',   colors: ['#0f3a5f', '#2f80b2', '#7fa05a', '#cdbf6e', '#9c6f3f', '#efe9df'] },
      { id: 'sand',    label: 'Arid',    colors: ['#173f57', '#4a8aa0', '#e6d7ab', '#cdb27e', '#a07b4a', '#75522e'] },
      { id: 'ocean',   label: 'Blue',    colors: ['#04203a', '#0e4d76', '#3a86b5', '#9fcfe6', '#cfe7f2', '#eef7fb'] },
      { id: 'mono',    label: 'Mono',    colors: ['#1c1c1c', '#4a4a4a', '#8c8c8c', '#b8b8b8', '#dadada', '#f4f4f4'] },
    ],
    params: [
      { id: 'scale',    label: 'Scale',     min: 400, max: 6000, step: 100,  defaultValue: 1800 },
      { id: 'seaLevel', label: 'Sea level', min: 0.0, max: 0.7,  step: 0.02, defaultValue: 0.46 },
      { id: 'warp',     label: 'Warp',      min: 0.0, max: 5.0,  step: 0.25, defaultValue: 2.0  },
      { id: 'contour',  label: 'Contours',  min: 3,   max: 20,   step: 1,    defaultValue: 7    },
    ],
  },
  {
    id: 'riverNetwork',
    label: 'River Delta',
    description: 'Coherent drainage basins: meandering trunk rivers that fan into deltas at the coast.',
    defaultSeed: 77,
    defaultPalette: 'delta',
    // World ramps: [0]=deep water [1]=river/shallow water
    // [2]=lowland [3]=upland [4]=highland [5]=peak.
    palettes: [
      { id: 'delta',   label: 'Delta',   colors: ['#0a2f3a', '#46c5dd', '#9aa06e', '#6f7742', '#49512c', '#2c3318'] },
      { id: 'arid',    label: 'Arid',    colors: ['#13323a', '#2f6f6a', '#e3d3a0', '#c2a368', '#8f6a3a', '#5e4422'] },
      { id: 'glacier', label: 'Glacier', colors: ['#0c2f4d', '#2f6f9c', '#dfeef5', '#bcd2de', '#8fb0c4', '#5f7e96'] },
      { id: 'ink',     label: 'Ink',     colors: ['#0a0a0a', '#2a2a2a', '#f2f2ee', '#cfcfc8', '#8f8f88', '#4a4a44'] },
    ],
    params: [
      { id: 'scale',     label: 'Scale',      min: 400, max: 6000, step: 100,  defaultValue: 1800 },
      { id: 'threshold', label: 'Min stream', min: 0.05, max: 0.7, step: 0.02, defaultValue: 0.24 },
      { id: 'seaLevel',  label: 'Sea level',  min: 0.0, max: 0.7,  step: 0.02, defaultValue: 0.36 },
      { id: 'warp',      label: 'Warp',       min: 0.0, max: 5.0,  step: 0.25, defaultValue: 1.5  },
    ],
  },
  {
    id: 'haze',
    label: 'Haze',
    description: 'A calm, low-contrast tonal wash — slow cloud-soft colour drift with no detail to compete with your windows.',
    defaultSeed: 12,
    defaultPalette: 'slate',
    // Smooth ramp, low→high tone: all six entries are close in value so the
    // field reads as a gentle wash rather than a map. [0]=darkest … [5]=lightest.
    palettes: [
      { id: 'slate',  label: 'Slate',  colors: ['#1b2430', '#243140', '#2f3e50', '#3b4c60', '#475a70', '#54697f'] },
      { id: 'paper',  label: 'Paper',  colors: ['#e7e1d6', '#ece6db', '#f0ebe1', '#f4efe7', '#f8f4ed', '#fbf8f2'] },
      { id: 'sage',   label: 'Sage',   colors: ['#222d28', '#2c3a33', '#374740', '#43554d', '#50635a', '#5e7268'] },
      { id: 'dusk',   label: 'Dusk',   colors: ['#211e2e', '#2a2740', '#343150', '#403c61', '#4d4873', '#5b5685'] },
    ],
    params: [
      { id: 'scale',    label: 'Scale',    min: 800, max: 8000, step: 100,  defaultValue: 3500 },
      { id: 'warp',     label: 'Warp',     min: 0.0, max: 4.0,  step: 0.25, defaultValue: 1.2  },
      { id: 'contrast', label: 'Contrast', min: 0.2, max: 3.0,  step: 0.05, defaultValue: 1.1  },
    ],
  },
  {
    id: 'contours',
    label: 'Contours',
    description: 'Quiet drafting surface: thin topographic isolines drifting over a near-solid muted ground. No fills, no shading.',
    defaultSeed: 23,
    defaultPalette: 'blueprint',
    // [0]=ground [1]=ground tint [2]=minor line [3]=major line [4],[5]=accents.
    palettes: [
      { id: 'blueprint', label: 'Blueprint', colors: ['#12314f', '#173a5c', '#3f6f9a', '#a9c9e4', '#6f9ac0', '#cfe2f2'] },
      { id: 'drafting',  label: 'Drafting',  colors: ['#efe9dd', '#e9e2d2', '#b9a986', '#8a7858', '#a99877', '#73613f'] },
      { id: 'slate',     label: 'Slate',     colors: ['#222a33', '#27313c', '#4a5b6c', '#92a6b8', '#647889', '#b7c7d4'] },
      { id: 'mono',      label: 'Mono',      colors: ['#16161a', '#1c1c20', '#46464c', '#8c8c94', '#5e5e64', '#b6b6bc'] },
    ],
    params: [
      { id: 'scale',   label: 'Scale',    min: 400, max: 6000, step: 100, defaultValue: 1600 },
      { id: 'warp',    label: 'Warp',     min: 0.0, max: 5.0,  step: 0.25, defaultValue: 1.5 },
      { id: 'contour', label: 'Contours', min: 4,   max: 40,   step: 1,    defaultValue: 16  },
    ],
  },
  {
    id: 'dotGrid',
    label: 'Dot Grid',
    description: 'A functional desktop matrix: a fine world-anchored dot grid with major accents that subdivide and fade with zoom.',
    defaultSeed: 0,
    defaultPalette: 'paper',
    // [0]=ground [1]=(unused) [2]=minor dot [3]=major dot [4],[5]=accents.
    palettes: [
      { id: 'paper',     label: 'Paper',     colors: ['#f5f3ee', '#f5f3ee', '#c8c4b8', '#a8a294', '#c8c4b8', '#a8a294'] },
      { id: 'slate',     label: 'Slate',     colors: ['#1f2630', '#1f2630', '#384350', '#5a6878', '#384350', '#5a6878'] },
      { id: 'blueprint', label: 'Blueprint', colors: ['#15324f', '#15324f', '#34597a', '#7fa6c8', '#34597a', '#7fa6c8'] },
      { id: 'sage',      label: 'Sage',      colors: ['#eef0ea', '#eef0ea', '#c2cabb', '#9aa68f', '#c2cabb', '#9aa68f'] },
    ],
    params: [
      { id: 'spacing', label: 'Spacing',     min: 16, max: 128, step: 4,   defaultValue: 40 },
      { id: 'dotSize', label: 'Dot size',    min: 1,  max: 6,   step: 0.5, defaultValue: 2  },
      { id: 'major',   label: 'Major every', min: 4,  max: 12,  step: 1,   defaultValue: 5  },
    ],
  },
  {
    id: 'cuttingMat',
    label: 'Cutting Mat',
    description: 'A self-healing cutting-mat work surface: fine measurement grid, bold majors, 45° angle guides and concentric curves.',
    defaultSeed: 0,
    defaultPalette: 'green',
    // [0]=mat base [1]=angle/curve line [2]=minor grid [3]=major grid [4],[5]=accents.
    palettes: [
      { id: 'green', label: 'Green', colors: ['#244438', '#6f9d86', '#3c6353', '#c9e3d2', '#8fc0a8', '#1b352c'] },
      { id: 'blue',  label: 'Blue',  colors: ['#1e3a4d', '#5a8298', '#345a6e', '#cfe2ec', '#86acc0', '#152b39'] },
      { id: 'black', label: 'Black', colors: ['#1d1f1e', '#5a605c', '#333735', '#c9cec9', '#878d88', '#141615'] },
      { id: 'pink',  label: 'Pink',  colors: ['#5a2e44', '#a86c84', '#7d4a60', '#f0d3df', '#c897ab', '#462335'] },
    ],
    params: [
      { id: 'grid',   label: 'Grid',        min: 8,  max: 64, step: 2,    defaultValue: 16 },
      { id: 'major',  label: 'Major every', min: 4,  max: 12, step: 1,    defaultValue: 5  },
      { id: 'angles', label: 'Angle guides', min: 0.0, max: 1.0, step: 0.05, defaultValue: 0.5 },
      { id: 'curves', label: 'Curves',       min: 0.0, max: 1.0, step: 0.05, defaultValue: 0.4 },
    ],
  },
  {
    id: 'starfield',
    label: 'Astrogation',
    description: 'Deep-space star field with logarithmic-spiral galaxy arms and a faint polar navigation overlay — alien chart, calm background.',
    defaultSeed: 42,
    defaultPalette: 'deep',
    // [0]=void [1]=nebula haze [2]=arm glow [3]=star body [4]=nav lines [5]=star peak
    palettes: [
      { id: 'deep',   label: 'Deep',   colors: ['#080c18', '#101828', '#1c4a80', '#7ab0e0', '#c0d4f0', '#eef6ff'] },
      { id: 'amber',  label: 'Amber',  colors: ['#11100a', '#261f14', '#7a5018', '#c89848', '#e8d488', '#fff8d8'] },
      { id: 'nebula', label: 'Nebula', colors: ['#0c0814', '#1a0e2a', '#681858', '#4888cc', '#b89ade', '#f0ecff'] },
      { id: 'void',   label: 'Void',   colors: ['#090909', '#161616', '#424242', '#929292', '#c4c4c4', '#f8f8f8'] },
    ],
    params: [
      { id: 'starSpacing', label: 'Star density',   min: 80,   max: 400,   step: 20,  defaultValue: 180  },
      { id: 'galaxyScale', label: 'Galaxy scale',   min: 1500, max: 10000, step: 500, defaultValue: 5000 },
      { id: 'arms',        label: 'Spiral arms',    min: 1,    max: 4,     step: 1,   defaultValue: 3    },
      { id: 'navGrid',     label: 'Nav grid',       min: 0,    max: 1,     step: 0.05, defaultValue: 0.5  },
    ],
  },
  {
    id: 'arabesque',
    label: 'Dome',
    description: 'Persian dome geometry: rings of pointed medallions spiralling into a central sun (shamsa), woven with a gold interlace — after the ceiling of the Imam Mosque, Isfahan.',
    defaultSeed: 7,
    defaultPalette: 'isfahan',
    // [0]=deep ground [1]=turquoise/ring [2]=cream fill [3]=gold
    // [4]=coral accent [5]=highlight (pale gold / silver).
    palettes: [
      { id: 'isfahan', label: 'Isfahan', colors: ['#0e357e', '#1aa5a0', '#f2e6c2', '#e0b23a', '#c2532b', '#fdf6dc'] },
      { id: 'lapis',   label: 'Lapis',   colors: ['#0f1f56', '#25408a', '#e9ddbd', '#caa24a', '#c33f2d', '#e4ebf2'] },
      { id: 'emerald', label: 'Emerald', colors: ['#0b3b2e', '#1f8f6b', '#efe6c6', '#d8aa3c', '#b7492c', '#f6efd4'] },
      { id: 'ivory',   label: 'Ivory',   colors: ['#ece4d3', '#d8c9a8', '#fbf7ee', '#b9962f', '#9c7b52', '#ffffff'] },
    ],
    params: [
      { id: 'scale',    label: 'Scale',    min: 300, max: 3000, step: 50,   defaultValue: 900 },
      { id: 'symmetry', label: 'Symmetry', min: 6,   max: 24,   step: 1,    defaultValue: 18  },
      { id: 'rings',    label: 'Rings',    min: 1.0, max: 6.0,  step: 0.1,  defaultValue: 3.0 },
      { id: 'twist',    label: 'Twist',    min: 0.0, max: 0.6,  step: 0.02, defaultValue: 0.12 },
    ],
  },
  {
    id: 'domeV2',
    label: 'Dome II',
    description: 'A real, flat Islamic geometric tessellation: a seamless khatam lattice of sixteen-point suns, petals, darts and interlocking corner stars — the traditional square-and-arc construction.',
    defaultSeed: 0,
    defaultPalette: 'isfahan',
    // [0]=ground [1]=turquoise [2]=cream [3]=gold [4]=coral [5]=highlight.
    palettes: [
      { id: 'isfahan', label: 'Isfahan', colors: ['#123c8c', '#1aa39b', '#f2e6c2', '#e6b53a', '#c1512b', '#eaf3f6'] },
      { id: 'lapis',   label: 'Lapis',   colors: ['#0f1f56', '#2452a8', '#ece0be', '#d0a743', '#c33f2d', '#dfe7ef'] },
      { id: 'emerald', label: 'Emerald', colors: ['#0b3b2e', '#1f9068', '#efe6c6', '#dcac3b', '#b7492c', '#eef6ea'] },
      { id: 'ivory',   label: 'Ivory',   colors: ['#e7ddc6', '#c9b48a', '#fbf7ee', '#b8912b', '#9c6b3f', '#ffffff'] },
    ],
    params: [
      { id: 'scale',  label: 'Scale',   min: 300, max: 1600, step: 20,   defaultValue: 640  },
      { id: 'stroke', label: 'Outline', min: 0,   max: 14,   step: 0.5,  defaultValue: 4    },
      { id: 'lines',  label: 'Guides',  min: 0.0, max: 1.0,  step: 0.05, defaultValue: 0.35 },
    ],
  },
];

export function getGenerator(id: string): GeneratorDescriptor | undefined {
  return GENERATORS.find(g => g.id === id);
}

/** Build a default SpaceBackground for a given generator id. */
export function defaultProceduralBackground(generatorId: GeneratorId): SpaceBackground {
  const gen = getGenerator(generatorId);
  if (!gen) throw new Error(`Unknown generator: ${generatorId}`);
  const params: Record<string, number> = {};
  gen.params.forEach(p => { params[p.id] = p.defaultValue; });
  return {
    type: 'procedural',
    generator: generatorId,
    seed: gen.defaultSeed,
    palette: gen.defaultPalette,
    params,
  };
}
